import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import SectionHeader from "@/components/feature/SectionHeader";
import HeroCarousel from "./components/HeroCarousel";
import CategoryGrid from "./components/CategoryGrid";
import { usePageMeta } from "@/hooks/usePageMeta";
import { allAnime, formatDate, formatViews, getFeatured, getLatest, getPopular } from "@/utils/anime";
import { categories } from "@/mocks/animeData";

const HOME_TDK = {
  title: "色情动漫 - 最新成人动漫作品及内容资源聚合",
  description:
    "色情动漫网站汇聚丰富的成人动漫作品和最新资源，为用户提供多样化的动漫内容介绍和分类浏览，满足动漫爱好者的多元需求。",
  keywords: "色情动漫,成人动漫,动漫资源,动漫作品,动漫分类,动漫介绍,动画片,动漫推荐",
};

export default function Home() {
  usePageMeta(HOME_TDK);

  const featured = useMemo(() => getFeatured(), []);
  const latest = useMemo(() => getLatest(20), []);
  const popular = useMemo(() => getPopular(6), []);
  const ranking = useMemo(() => getPopular(5), []);
  const [visible, setVisible] = useState(10);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full">
        <HeroCarousel items={featured} />

        <section className="w-full border-b border-background-200/70 bg-background-100/80">
          <div className="w-full px-4 md:px-8 lg:px-12 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 justify-between">
            <p className="text-xs text-foreground-500 flex items-center gap-2">
              <i className="ri-error-warning-line text-accent-500" />
              内容提示：本站面向 18 岁以上用户，仅提供作品资料介绍，不含播放与下载。
            </p>
            <p className="text-xs text-foreground-400">
              共收录 <span className="text-accent-400 font-label tracking-wider">{allAnime.length}</span> 部作品 ·
              <span className="ml-1">{categories.length} 个分类</span>
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <SectionHeader
            eyebrow="GENRE"
            title="按分类快速进入"
            description="从题材出发，找到今晚想看的那一部。每个分类都按更新与热度整理好了顺序。"
            linkTo="/category"
            linkLabel="进入完整分类页"
          />
          <CategoryGrid />
        </section>

        <section className="w-full bg-background-100/50 border-y border-background-200/60">
          <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
            <SectionHeader
              eyebrow="LATEST UPDATE"
              title="最新上架与更新"
              description="按更新时间倒序排列，含连载进度与最新一集上线日期。"
              linkTo="/latest"
              linkLabel="查看全部更新"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-7">
              {latest.slice(0, visible).map((item) => (
                <AnimeCard key={item.id} anime={item} />
              ))}
            </div>
            {visible < latest.length && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => Math.min(prev + 5, latest.length))}
                  className="h-11 px-8 rounded-md border border-accent-600/50 text-accent-400 hover:bg-accent-500/10 text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                >
                  加载更多作品
                  <i className="ri-arrow-down-line" />
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <SectionHeader
            eyebrow="POPULAR"
            title="本周热门推荐"
            description="根据站内浏览与收藏数据模拟的热度排行，每周更新一次。"
            linkTo="/popular"
            linkLabel="完整热门榜单"
          />
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-7">
              {popular.map((item, index) => (
                <AnimeCard key={item.id} anime={item} showRank={index + 1} />
              ))}
            </div>

            <aside className="rounded-lg border border-background-300/50 bg-background-100/70 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm text-foreground-900 tracking-wide flex items-center gap-2">
                  <i className="ri-fire-line text-accent-500" />
                  热度榜 TOP 5
                </h3>
                <Link to="/popular" className="text-[11px] text-foreground-500 hover:text-accent-400 transition-colors">
                  更多
                </Link>
              </div>
              <ul className="flex flex-col">
                {ranking.map((item, index) => (
                  <li key={item.id} className="border-b border-background-200/60 last:border-b-0">
                    <Link to={`/detail/${item.id}`} className="flex items-center gap-3 py-3 group">
                      <span
                        className={`w-6 font-label text-lg text-center ${
                          index < 3 ? "text-accent-500" : "text-foreground-500"
                        }`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <img
                        src={item.cover}
                        alt={item.title}
                        className="w-10 h-14 rounded object-cover object-top border border-background-300/40"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground-900 truncate group-hover:text-accent-400 transition-colors">
                          {item.title}
                        </p>
                        <p className="mt-1 text-[11px] text-foreground-500">
                          {formatViews(item.views)} 浏览 · {item.status}
                        </p>
                        <p className="mt-0.5 text-[11px] text-foreground-400">{formatDate(item.updatedAt)}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-500 group-hover:text-accent-400 transition-colors" />
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 pb-4">
          <div className="rounded-lg border border-background-300/50 card-glow px-6 py-10 md:px-12 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <p className="text-[11px] tracking-[0.3em] text-accent-500 mb-3">ABOUT THE ARCHIVE</p>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug">
                  一个只做资料整理与索引的
                  <br className="hidden md:block" />
                  成人向动画档案站
                </h2>
                <p className="mt-4 text-sm text-foreground-500 leading-relaxed max-w-xl">
                  我们把散落在各处的作品信息整理成统一的结构：类型、地区、年份、更新状态、标签与简介。
                  你可以用分类筛选或关键词搜索快速定位，也可以把感兴趣的作品收藏在本地，下次回来继续看。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/about"
                    className="h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-400 text-background-50 text-sm flex items-center transition-colors whitespace-nowrap"
                  >
                    了解本站
                  </Link>
                  <Link
                    to="/category"
                    className="h-10 px-5 rounded-md border border-background-400/60 text-foreground-700 hover:text-accent-400 hover:border-accent-500/70 text-sm flex items-center transition-colors whitespace-nowrap"
                  >
                    开始分类浏览
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: `${allAnime.length}`, label: "收录作品" },
                  { value: `${categories.length}`, label: "内容分类" },
                  { value: "2026", label: "持续更新" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-md border border-background-300/50 bg-background-50/60 px-3 py-5 text-center">
                    <p className="font-label text-3xl text-accent-400 leading-none">{stat.value}</p>
                    <p className="mt-2 text-[11px] text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}